const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');
const STORAGE_KEY = 'tvx_marketing_ctx';
const COOKIE_KEY = 'tvx_marketing_ctx';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;
const ROOT_DOMAIN = '.trivox.sh';
const TRACKING_QUERY_KEYS = ['utm_campaign', 'utm_source', 'utm_medium', 'utm_content', 'campaignId', 'campaign_id', 'sessionId', 'session_id'];

function apiUrl(path) {
  const p = String(path || '').startsWith('/') ? path : `/${path}`;
  return API_BASE ? `${API_BASE}${p}` : p;
}

function safeNow() {
  return new Date().toISOString();
}

function createSessionId() {
  const random = Math.random().toString(36).slice(2, 12);
  return `mkt_${Date.now().toString(36)}_${random}`;
}

function normalizeText(value, max = 180) {
  return String(value || '').trim().slice(0, max);
}

function normalizeCampaignId(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function readCookieValue(name) {
  if (typeof document === 'undefined') return null;
  const encodedName = `${encodeURIComponent(name)}=`;
  const parts = document.cookie ? document.cookie.split('; ') : [];
  for (const part of parts) {
    if (!part.startsWith(encodedName)) continue;
    return part.slice(encodedName.length);
  }
  return null;
}

function writeCookieValue(name, value) {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${encodeURIComponent(name)}=${value}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax; Domain=${ROOT_DOMAIN}${secure}`;
}

function decodeContext(raw) {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed;
  } catch (_) {
    return {};
  }
}

function readLocalContext() {
  if (typeof window === 'undefined') return {};
  try {
    return decodeContext(localStorage.getItem(STORAGE_KEY));
  } catch (_) {
    return {};
  }
}

function readCookieContext() {
  const raw = readCookieValue(COOKIE_KEY);
  if (!raw) return {};
  try {
    return decodeContext(atob(decodeURIComponent(raw)));
  } catch (_) {
    return {};
  }
}

function readContextFromUrl() {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const contextFromUrl = {};

  const campaignIdRaw = params.get('campaignId') || params.get('campaign_id');
  if (campaignIdRaw) {
    contextFromUrl.campaignId = normalizeCampaignId(campaignIdRaw);
  }

  const utmCampaign = normalizeText(params.get('utm_campaign'), 180);
  if (utmCampaign) contextFromUrl.utmCampaign = utmCampaign;

  const utmSource = normalizeText(params.get('utm_source'), 100);
  if (utmSource) contextFromUrl.utmSource = utmSource;

  const utmMedium = normalizeText(params.get('utm_medium'), 100);
  if (utmMedium) contextFromUrl.utmMedium = utmMedium;

  const utmContent = normalizeText(params.get('utm_content'), 180);
  if (utmContent) contextFromUrl.utmContent = utmContent;

  const sessionId = normalizeText(params.get('sessionId') || params.get('session_id'), 120);
  if (sessionId) contextFromUrl.sessionId = sessionId;

  return contextFromUrl;
}

function stripTrackingParamsFromUrl() {
  if (typeof window === 'undefined') return;
  if (!window.location.search) return;

  const params = new URLSearchParams(window.location.search);
  let changed = false;
  for (const key of TRACKING_QUERY_KEYS) {
    if (params.has(key)) {
      params.delete(key);
      changed = true;
    }
  }

  if (!changed) return;
  const nextQuery = params.toString();
  const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash || ''}`;
  window.history.replaceState(window.history.state, '', nextUrl);
}

function persistContext(context) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(context));
  } catch (_) {}

  try {
    const raw = btoa(JSON.stringify(context));
    writeCookieValue(COOKIE_KEY, encodeURIComponent(raw));
  } catch (_) {}
}

function hasAttributionContext(ctx) {
  return Boolean(ctx.campaignId || ctx.utmCampaign || ctx.utmSource || ctx.utmMedium || ctx.utmContent);
}

export function getMarketingContext() {
  const local = readLocalContext();
  const cookie = readCookieContext();
  const fromUrl = readContextFromUrl();
  if (Object.keys(fromUrl).length > 0) {
    stripTrackingParamsFromUrl();
  }
  const merged = {
    ...cookie,
    ...local,
    ...fromUrl,
  };

  const now = safeNow();
  const context = {
    sessionId: normalizeText(merged.sessionId, 120) || createSessionId(),
    campaignId: normalizeCampaignId(merged.campaignId),
    utmCampaign: normalizeText(merged.utmCampaign, 180).toLowerCase(),
    utmSource: normalizeText(merged.utmSource, 100).toLowerCase(),
    utmMedium: normalizeText(merged.utmMedium, 100).toLowerCase(),
    utmContent: normalizeText(merged.utmContent, 180),
    firstSeenAt: normalizeText(merged.firstSeenAt, 40) || now,
    lastSeenAt: now,
  };

  persistContext(context);
  return context;
}

export async function trackAffiliateClick(code) {
  const normalized = String(code || '').trim();
  if (!normalized || typeof window === 'undefined') return { tracked: false };
  const sessionKey = `tvx_aff_click_${normalized.toUpperCase()}`;
  if (sessionStorage.getItem(sessionKey)) return { tracked: false, reason: 'already-counted' };
  sessionStorage.setItem(sessionKey, '1');
  try { sessionStorage.setItem('tvx_aff_code', normalized.toUpperCase()); } catch (_) {}
  try {
    await fetch(apiUrl('/api/affiliate/click'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: normalized }),
      keepalive: true,
    });
    return { tracked: true };
  } catch (_) {
    return { tracked: false };
  }
}

export async function trackMarketingEvent(eventType, extras = {}) {
  if (typeof window === 'undefined') return { tracked: false, reason: 'ssr' };

  const context = getMarketingContext();
  const shouldTrack = hasAttributionContext(context) || eventType !== 'visit';
  if (!shouldTrack) {
    return { tracked: false, reason: 'missing-attribution' };
  }

  const payload = {
    eventType,
    campaignId: context.campaignId || null,
    utmCampaign: context.utmCampaign || '',
    utmSource: context.utmSource || '',
    utmMedium: context.utmMedium || '',
    utmContent: context.utmContent || '',
    sessionId: context.sessionId,
    pagePath: `${window.location.pathname}${window.location.search}`,
    referrer: document.referrer || '',
    ...extras,
  };

  try {
    const response = await fetch(apiUrl('/api/marketing/track'), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });

    if (!response.ok) {
      console.warn('[landing-marketing] track failed', eventType, response.status);
      return { tracked: false, reason: `http-${response.status}` };
    }

    console.info('[landing-marketing] tracked', eventType, {
      campaignId: payload.campaignId,
      utmCampaign: payload.utmCampaign,
      pagePath: payload.pagePath,
    });
    return { tracked: true };
  } catch (error) {
    console.warn('[landing-marketing] track error', eventType, error);
    return { tracked: false, reason: 'network' };
  }
}
